/**
 * Multi-topic SSE client singleton.
 *
 * Why a singleton: browsers cap concurrent connections per origin to ~6 across
 * the whole tab. With each panel opening its own EventSource we'd burn through
 * that cap quickly. One shared connection multiplexed by topic is the right
 * shape for an internal admin dashboard.
 *
 * Public API
 * ----------
 *   const unsub = eventStream.subscribe("queue_health", (payload) => ...)
 *   unsub()                                  // tear it down
 *   eventStream.onLiveStateChange((state) => ...)   // 'connecting' | 'live' | 'polling' | 'offline'
 *
 * Behaviour
 * ---------
 * - Lazily opens the connection on first subscribe; closes it on last unsub.
 * - If subscriptions change while connected, rebuilds the URL and reconnects.
 * - Pauses when the tab is hidden (Page Visibility API); resumes on focus.
 * - Exponential backoff on errors (1s / 3s / 7s); after 3 failures falls back
 *   to one-shot polling per topic via `pollFallback(topic)` if the caller
 *   provided one to subscribe(). Otherwise just gives up quietly.
 */

const KNOWN_LIVE_STATES = new Set(["connecting", "live", "polling", "offline"]);

class EventStream {
  constructor() {
    this.subs = new Map();         // topic → Set<callback>
    this.fallbacks = new Map();    // topic → async () => snapshot
    this.liveListeners = new Set();
    this.es = null;
    this.failures = 0;
    this.liveState = "offline";
    this.fallbackTimers = new Map(); // topic → setInterval handle
    this._visListenerBound = false;
    this._reconnectTimer = null;
  }

  /** Subscribe to a topic. Returns an unsubscribe function. */
  subscribe(topic, callback, pollFallback) {
    if (!this.subs.has(topic)) this.subs.set(topic, new Set());
    this.subs.get(topic).add(callback);
    if (pollFallback) this.fallbacks.set(topic, pollFallback);

    this._ensureVisibilityListener();
    this._reconnectIfTopicSetChanged();

    return () => {
      const set = this.subs.get(topic);
      if (!set) return;
      set.delete(callback);
      if (set.size === 0) {
        this.subs.delete(topic);
        this.fallbacks.delete(topic);
        this._stopFallback(topic);
        this._reconnectIfTopicSetChanged();
      }
    };
  }

  /** Listen for live-state changes ('connecting'/'live'/'polling'/'offline'). */
  onLiveStateChange(callback) {
    this.liveListeners.add(callback);
    callback(this.liveState);
    return () => this.liveListeners.delete(callback);
  }

  // ---------- internals ----------------------------------------------------

  _setLive(state) {
    if (!KNOWN_LIVE_STATES.has(state)) return;
    if (this.liveState === state) return;
    this.liveState = state;
    this.liveListeners.forEach((cb) => {
      try { cb(state); } catch { /* listeners must not throw */ }
    });
  }

  _topicListKey() {
    return Array.from(this.subs.keys()).sort().join(",");
  }

  _ensureVisibilityListener() {
    if (this._visListenerBound || typeof document === "undefined") return;
    this._visListenerBound = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this._closeStream();
        this._setLive("offline");
      } else if (this.subs.size > 0 && !this.es) {
        this.failures = 0;
        this._openStream();
      }
    });
  }

  _reconnectIfTopicSetChanged() {
    if (this.subs.size === 0) {
      this._closeStream();
      this._setLive("offline");
      return;
    }
    // If a stream is open with a different topic set, close + reopen.
    const desired = this._topicListKey();
    if (this.es && this.es.__topicKey !== desired) {
      this._closeStream();
    }
    if (!this.es && (!document || document.visibilityState !== "hidden")) {
      this._openStream();
    }
  }

  _openStream() {
    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      this._setLive("polling");
      this._startAllFallbacks();
      return;
    }
    if (this.subs.size === 0) return;
    this._stopAllFallbacks();
    this._setLive("connecting");

    const base = process.env.REACT_APP_BACKEND_URL || "";
    const topicKey = this._topicListKey();
    const url = `${base}/api/admin/events/stream?topics=${encodeURIComponent(topicKey)}`;
    const es = new EventSource(url, { withCredentials: true });
    es.__topicKey = topicKey;
    this.es = es;

    // Register per-topic listeners
    for (const topic of this.subs.keys()) {
      es.addEventListener(topic, (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          this.subs.get(topic)?.forEach((cb) => {
            try { cb(payload); } catch { /* keep going */ }
          });
          this._setLive("live");
          this.failures = 0;
        } catch { /* malformed payload — wait for next */ }
      });
    }

    es.addEventListener("ping", () => this._setLive("live"));

    es.addEventListener("bye", () => {
      // Server hit session cap; reconnect on the next tick.
      this._closeStream();
      this._reconnectTimer = setTimeout(() => this._openStream(), 250);
    });

    es.onerror = () => {
      this.failures += 1;
      this._closeStream();
      if (this.failures >= 3) {
        // Persistent failure → degrade to polling.
        this._setLive("polling");
        this._startAllFallbacks();
        return;
      }
      const backoffMs = 1000 * (2 ** this.failures - 1); // 1s, 3s, 7s
      this._reconnectTimer = setTimeout(() => this._openStream(), backoffMs);
    };
  }

  _closeStream() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this.es) {
      try { this.es.close(); } catch { /* ignore */ }
      this.es = null;
    }
  }

  // ---------- polling fallback per topic -----------------------------------

  _startAllFallbacks() {
    for (const topic of this.subs.keys()) this._startFallback(topic);
  }

  _stopAllFallbacks() {
    for (const topic of Array.from(this.fallbackTimers.keys())) this._stopFallback(topic);
  }

  _startFallback(topic) {
    if (this.fallbackTimers.has(topic)) return;
    const fn = this.fallbacks.get(topic);
    if (!fn) return;
    const tick = async () => {
      try {
        const payload = await fn();
        this.subs.get(topic)?.forEach((cb) => {
          try { cb(payload); } catch { /* ignore */ }
        });
      } catch { /* swallow — next tick will retry */ }
    };
    tick();
    const handle = setInterval(tick, 30000);
    this.fallbackTimers.set(topic, handle);
  }

  _stopFallback(topic) {
    const h = this.fallbackTimers.get(topic);
    if (h) {
      clearInterval(h);
      this.fallbackTimers.delete(topic);
    }
  }
}

// Singleton, lazily allocated per module load. Tests can reset via __reset()
const eventStream = new EventStream();
export default eventStream;
